import Link from 'next/link';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';

import { FeatureFlags } from 'featureFlags/constants';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { Image } from 'shared/components/Image';
import { ITabName } from 'checkout/types/Tab';
import { localizeCondition } from 'shared/utils/localizeCondition';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { OrderSummaryPriceBreakdown } from 'checkout/components/GlobalPricingBreakdown/OrderSummaryPriceBreakdown';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useGenderLabel } from 'shared/hooks/useGenderLabel';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular, variableFontMedium } from 'styles/theme/text';

const TP = 'checkout.components.Summary.OrderSummary';

interface IOrderSummaryProps {
  className?: string;
  showPriceBreakdown?: boolean;
  showSupportLinks?: boolean;
  showTitle?: boolean;
  isModal?: boolean;
}

export const OrderSummary = ({
  showPriceBreakdown = true,
  showSupportLinks = true,
  isModal = false,
}: IOrderSummaryProps) => {
  const getGenderLabel = useGenderLabel();
  const { addresses, order, tab, isGuestCheckout, want } = useCheckoutContext();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isOfferSettledInUsd = useFeatureFlag(FeatureFlags.TEMP_SETTLE_OFFERS_IN_USD);

  const shippingAddresses = Object.values(addresses).filter(
    (address) => address.addressType === 'shipping',
  );

  const isOffer = order?.wantId && isCheckoutOffersEnabled;
  let shouldShowGuestPriceBreakdown = true;

  /**
   * TODO: once price breakdown endpoint for guest checkout has been fixed remove
   * this logic
   */
  if (isGuestCheckout || !isAuthenticated) {
    shouldShowGuestPriceBreakdown = !isEmpty(shippingAddresses);
  }

  const handleTopicLinkClick = (topic: string) => {
    sendTrackingEvent('CHECKOUT_FAQ_TAP', {
      faq_topic: topic,
      product_template_slug: isOffer ? order?.product?.productTemplate?.slug : order?.product?.slug,
    });
  };

  const isSneakers =
    !order?.product?.productTemplate?.isFashionProduct ||
    order?.product?.productTemplate?.productType === 'sneakers' ||
    want?.productTemplate?.productType === 'sneakers';

  const boxCondition = isOffer ? want?.boxCondition : order?.product?.boxCondition;
  const mainPictureUrl =
    (isOffer ? want?.productTemplate?.mainPictureUrl : order?.product?.mainPictureUrl) || '';
  const name = isOffer ? want?.productTemplate?.name : order?.product?.productTemplate?.name;
  const presentation = isOffer
    ? want?.sizeOption?.presentation?.toUpperCase()
    : order?.product?.sizeOption?.presentation?.toUpperCase();
  const singleGender = isOffer
    ? want?.productTemplate?.singleGender
    : order?.product?.productTemplate?.singleGender;
  const sizeUnit = isOffer
    ? want?.productTemplate?.sizeUnit?.toUpperCase()
    : order?.product?.productTemplate?.sizeUnit?.toUpperCase();
  const variantShoeCondition = isOffer
    ? want?.variantShoeCondition
    : order?.product?.variantShoeCondition;

  return (
    <OrderSummary.Container $isModal={isModal}>
      {!isModal && (
        <OrderSummary.Title data-qa="checkout_order_summary_header">
          {isOffer
            ? t(`${TP}.orderReviewOfferTitle`, 'Your Offer')
            : t(`${TP}.orderReviewOrderTitle`, 'Your Order')}
        </OrderSummary.Title>
      )}
      <OrderSummary.OrderTable>
        <OrderSummary.ItemImage>
          <Image
            alt={normalizeValue(name)}
            data-qa="checkout_order_summary_image"
            src={mainPictureUrl}
            disableLazyLoad
            sizes="90px"
          />
        </OrderSummary.ItemImage>
        <OrderSummary.ItemDescription>
          <p>{name}</p>
          <p>
            {t(
              `${TP}.ItemDescriptionSizeAndGender`,
              'Size {presentation} {sizeUnit} {singleGender} ({condition})',
              {
                presentation,
                sizeUnit,
                singleGender: startCase(getGenderLabel(singleGender), {
                  capitalizeInOtherLanguages: false,
                }),
                condition:
                  variantShoeCondition === SHOE_CONDITIONS.NEW_NO_DEFECTS
                    ? startCase(localizeCondition(SHOE_CONDITIONS.NEW), {
                        capitalizeInOtherLanguages: false,
                      })
                    : startCase(localizeCondition(variantShoeCondition), {
                        capitalizeInOtherLanguages: false,
                      }),
              },
            )}
          </p>
          {boxCondition && isSneakers && (
            <p>
              {t(`${TP}.boxCondition`, 'Box: {condition}', {
                condition: startCase(localizeCondition(boxCondition), {
                  capitalizeInOtherLanguages: false,
                }),
              })}
            </p>
          )}
        </OrderSummary.ItemDescription>
        <OrderSummary.ItemPrice data-qa="checkout_order_summary_item_price">
          {formatMoneyObject(order?.localizedPriceCents || order?.localizedFinalPriceCents, {
            showInUsd: isOffer && isOfferSettledInUsd,
          })}
        </OrderSummary.ItemPrice>
        {tab !== ITabName.REVIEW && showPriceBreakdown && shouldShowGuestPriceBreakdown && (
          <OrderSummary.PriceBreakdown />
        )}
      </OrderSummary.OrderTable>
      {showSupportLinks && (
        <>
          <OrderSummary.HorizontalRule />
          <Link href="/buyer-protection-guarantee" passHref legacyBehavior>
            <OrderSummary.Links
              target="_blank"
              rel="noreferrer"
              onClick={() => handleTopicLinkClick('Buyer Protection Policy')}
              data-qa="checkout_order_summary_buyer_protection_link"
            >
              {t(`${TP}.buyerProtectionPolicy`, 'Buyer Protection Policy')}
            </OrderSummary.Links>
          </Link>

          <Link href="/returns" passHref legacyBehavior>
            <OrderSummary.Links
              target="_blank"
              rel="noreferrer"
              onClick={() => handleTopicLinkClick('Purchases & Returns Policy')}
              data-qa="checkout_order_summary_returns_link"
            >
              {t(`${TP}.returnPolicyLink`, 'Purchases & Returns Policy')}
            </OrderSummary.Links>
          </Link>

          <OrderSummary.Links
            href="https://support.goat.com/hc/en-us/requests/new"
            target="_blank"
            rel="noreferrer"
            data-qa="checkout_order_summary_support_link"
            onClick={() => handleTopicLinkClick('Contact Support')}
          >
            {t(`${TP}.contactSupportLink`, 'Contact Support')}
          </OrderSummary.Links>
        </>
      )}
    </OrderSummary.Container>
  );
};

OrderSummary.Container = styled.div<{ $isModal?: boolean }>`
  max-width: 428px;
  padding: 56px 16px 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    max-width: unset;
    margin-top: 48px;
    padding: 0 16px;

    ${({ $isModal }) =>
      $isModal &&
      `
      margin-top: 0px;
      padding: 0;
    `}
  }

  ${({ $isModal }) =>
    $isModal &&
    `
    margin-top: 0px;
    padding: 0;
  `}
`;

OrderSummary.OrderTable = styled.div`
  ${variableFontRegular({ size: '11.5px', fontWeight: '400' })};
  display: grid;
  margin-top: 16px;
  grid-template-columns: [image-start] 90px [image-end] auto [description] 55px [price];
  grid-template-rows: [item-start] auto [item-end] auto [total];
`;

OrderSummary.ItemImage = styled.div`
  grid-column-start: image-start;
  grid-column-end: image-end;
  grid-row-start: item-start;
  grid-row-end: item-end;
  margin-bottom: 16px;
`;

OrderSummary.ItemDescription = styled.div`
  grid-column-start: image-end;
  grid-column-end: description;
  grid-row-start: item-start;
  grid-row-end: item-end;
  padding-top: 16px;
  padding-left: 24px;

  > p {
    margin: 0 0 8px;
  }
`;

OrderSummary.ItemSize = styled.span`
  display: block;
  margin-top: 8px;
`;

OrderSummary.ItemPrice = styled.div`
  grid-column-start: description;
  grid-column-end: price;
  grid-row-start: item-start;
  grid-row-end: item-end;
  padding-top: 16px;
  text-align: right;
`;

OrderSummary.TotalDropdown = styled.div`
  grid-column-start: image-start;
  grid-column-end: description;
  grid-row-start: item-end;
  grid-row-end: total;
  padding: 16px 0;
`;

OrderSummary.TotalPrice = styled.div`
  grid-column-start: description;
  grid-column-end: price;
  grid-row-start: item-end;
  grid-row-end: total;
  padding-top: 16px;
  text-align: right;
`;

OrderSummary.Links = styled.a`
  display: block;
  text-decoration: underline;
  line-height: 14px;
  margin-bottom: 4px;
  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin-bottom: 8px;
  }
`;

// padding to match v padding in checkout tabs for alignment
OrderSummary.Title = styled.div`
  padding: 8px 0;
  ${variableFontMedium({ size: '11.5px' })};
`;

OrderSummary.HorizontalRule = styled.hr`
  margin: 16px 0 24px 0;
  opacity: 1;
`;

OrderSummary.PriceBreakdown = styled(OrderSummaryPriceBreakdown)`
  grid-column-start: image-start;
  grid-column-end: price;
  padding-top: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding: 0;
  }
`;
